import React, { useState, useEffect, useRef } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas } from "@fortawesome/free-solid-svg-icons"
library.add(fas)

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { getRequest, pathname } from "@react/helpers/fetch"
import Card from "@react/shared/ui/Card"

const REVISION_NO = "no"

const ClientPicker = ({ value, onChange, searchUrl }) => {
  const [query,   setQuery]   = useState(value?.name || "")
  const [results, setResults] = useState([])
  const [open,    setOpen]    = useState(false)
  const [loading, setLoading] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => { setQuery(value?.name || "") }, [value?.id])

  useEffect(() => {
    const handler = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const search = term => {
    setLoading(true)
    getRequest(pathname({ path: "/clients/autocomplete", format: "json", params: term ? { q: term } : {} }))
      .then(data => setResults(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false))
  }

  const onFocus = () => { setOpen(true); if (results.length === 0) search("") }
  const onInput = e => { setQuery(e.target.value); search(e.target.value); setOpen(true) }
  const pick    = item => { onChange({ id: item.id, name: item.name }); setQuery(item.name); setOpen(false) }
  const clear   = () => { onChange(null); setQuery(""); setResults([]) }

  return (
    <div ref={wrapRef} className="relative">
      <FontAwesomeIcon icon="user-circle" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[12px]" />
      <input
        type        = "text"
        value       = {query}
        onFocus     = {onFocus}
        onChange    = {onInput}
        placeholder = {I18n.t("mobile.asset_select.select_client", { defaultValue: "Sélectionner un client" })}
        className   = "w-full h-10 pl-9 pr-9 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
        required
      />
      {value && (
        <button type="button" onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 w-6 h-6 flex items-center justify-center">
          <FontAwesomeIcon icon="xmark" />
        </button>
      )}
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-slate-500">…</div>}
          {!loading && results.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Aucun résultat</div>}
          {results.map(item => (
            <button
              key       = {item.id}
              type      = "button"
              onClick   = {() => pick(item)}
              className = "w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              {item.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const Field = ({ label, required, info, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-1">
      {label}{required && " *"}
      {info && <span className="ml-1 text-slate-400" tooltip={info}><FontAwesomeIcon icon="info-circle" /></span>}
    </label>
    {children}
  </div>
)

const TextInput = props => (
  <input
    {...props}
    className={`w-full h-10 px-3 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20 ${props.className || ""}`}
  />
)

const Select = ({ options, includeBlank, blankLabel, ...props }) => (
  <select
    {...props}
    className={`w-full h-10 px-3 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20 ${props.className || ""}`}
  >
    {includeBlank && <option value="">{blankLabel || "—"}</option>}
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
)

const App = ({ data }) => {
  if (!data) return null

  const isEdit = Boolean(data.mbc_contract.id)
  const c      = data.mbc_contract

  const [title,         setTitle]         = useState(c.title || "")
  const [contractCode,  setContractCode]  = useState(c.contract_code || "")
  const [status,        setStatus]        = useState(c.status || "draft")
  const [comment,       setComment]       = useState(c.comment || "")
  const [startDate,     setStartDate]     = useState(c.start_date_contract || "")
  const [endDate,       setEndDate]       = useState(c.end_date_contract || "")
  const [annualRevision, setAnnualRevision] = useState(c.annual_revision || REVISION_NO)
  const [formulaId,     setFormulaId]     = useState(c.formula_id || "")
  const [interval,      setInterval]      = useState(c.interval || 1)
  const [frequency,     setFrequency]     = useState(c.frequency || "")
  const [client,        setClient]        = useState(c.client || null)
  const [partnerId,     setPartnerId]     = useState(c.partner_id || "")
  const frozenId = c.partner_frozen ? c.partner_id : null
  const [lockedId,      setLockedId]      = useState(frozenId || c.client?.partner_id || null)
  const [tiers,         setTiers]         = useState(c.mbc_off_bpu_pricing_tiers || [])

  const [errors,     setErrors]     = useState(null)

  useEffect(() => {
    if (frozenId) return
    if (!client?.id) { setLockedId(null); return }
    getRequest(`/clients/${client.id}/build.json`).then(built => {
      const imposed = built?.partner?.id || null
      setLockedId(imposed)
      if (imposed) setPartnerId(imposed)
    })
  }, [client?.id])
  const [submitting, setSubmitting] = useState(false)

  const showRevision = annualRevision && annualRevision !== REVISION_NO

  const addTier = () => setTiers(prev => [...prev, { _temp: Date.now(), min_purchase_price: 0, max_purchase_price: null, coefficient: 1 }])
  const removeTier = idx => setTiers(prev => prev.map((t, i) => i === idx ? { ...t, _destroy: true } : t))
  const updateTier = (idx, field, value) => setTiers(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t))

  const validateTiers = () => {
    const active = tiers
      .map((t, idx) => ({ ...t, _idx: idx }))
      .filter(t => !t._destroy)
      .map(t => {
        const min = parseFloat(t.min_purchase_price)
        const max = t.max_purchase_price === "" || t.max_purchase_price == null
          ? Infinity
          : parseFloat(t.max_purchase_price)
        return { ...t, _min: min, _max: max }
      })

    for (const t of active) {
      if (isNaN(t._min)) {
        return `Tranche ${t._idx + 1} : prix minimum invalide`
      }
      if (t._max !== Infinity && isNaN(t._max)) {
        return `Tranche ${t._idx + 1} : prix maximum invalide`
      }
      if (t._max < t._min) {
        return `Tranche ${t._idx + 1} : le prix maximum doit être supérieur au prix minimum`
      }
    }

    for (let i = 0; i < active.length; i++) {
      for (let j = i + 1; j < active.length; j++) {
        const a = active[i]
        const b = active[j]
        if (a._min <= b._max && b._min <= a._max) {
          return `Les tranches ${a._idx + 1} et ${b._idx + 1} se chevauchent`
        }
      }
    }

    return null
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!client) { setErrors({ client: ["doit être sélectionné"] }); return }

    const tierError = validateTiers()
    if (tierError) {
      setErrors({ "Tarification hors BPU": [tierError] })
      window.scrollTo({ top: 0, behavior: "smooth" })
      return
    }

    setSubmitting(true)
    setErrors(null)

    const payload = {
      mbc_contract: {
        title,
        contract_code: contractCode,
        status,
        comment,
        client_id:           client.id,
        partner_id:          partnerId || null,
        start_date_contract: startDate,
        end_date_contract:   endDate || null,
        annual_revision:     annualRevision,
        formula_id:          formulaId || null,
        interval:            interval || null,
        frequency:           frequency || null,
        mbc_off_bpu_pricing_tiers_attributes: tiers
          .filter(t => !(t._destroy && !t.id))
          .map(t => ({
            id:                 t.id,
            _destroy:           t._destroy ? true : undefined,
            min_purchase_price: t.min_purchase_price,
            max_purchase_price: t.max_purchase_price === "" ? null : t.max_purchase_price,
            coefficient:        t.coefficient
          }))
      }
    }

    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const res  = await fetch(data.paths.submit, {
        method:  data.method,
        headers: {
          "Content-Type":     "application/json",
          "X-CSRF-Token":     csrf,
          "Accept":           "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body:        JSON.stringify(payload)
      })
      const body = await res.json().catch(() => null)
      if (res.ok && body?.redirect_to) {
        window.location.href = body.redirect_to
      } else {
        setErrors(body?.errors || { base: ["Erreur"] })
        setSubmitting(false)
      }
    } catch {
      setErrors({ base: ["Erreur réseau"] })
      setSubmitting(false)
    }
  }

  const errorMessages = errors ? Object.entries(errors).flatMap(([key, msgs]) =>
    Array.isArray(msgs) ? msgs.map(m => `${key} ${m}`) : [`${key} ${msgs}`]
  ) : []

  const visibleTiers = tiers.filter(t => !t._destroy)

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-900">
      <div className="px-8 py-8 max-w-5xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <a
            href      = {data.paths.cancel}
            className = "inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            title     = {I18n.t("button.back", { defaultValue: "Retour" })}
          >
            <FontAwesomeIcon icon="arrow-left" className="text-[13px]" />
          </a>
          <h1 className="text-2xl font-semibold text-slate-900">
            {isEdit
              ? title || I18n.t("button.edit", { defaultValue: "Modifier" })
              : I18n.t("mbc_contracts.new", { defaultValue: "Nouveau marché" })}
          </h1>
        </div>

        {errorMessages.length > 0 && (
          <Card padding="md" className="rounded-lg bg-rose-50 border-rose-200 mb-4">
            <div className="flex items-start gap-3 text-sm text-rose-800">
              <FontAwesomeIcon icon="circle-exclamation" className="text-rose-600 mt-0.5" />
              <ul className="space-y-0.5">
                {errorMessages.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Card padding="md" className="rounded-lg space-y-4">
            <Field label={I18n.t("navigation.client", { defaultValue: "Client" })} required>
              <ClientPicker value={client} onChange={setClient} searchUrl={data.paths.client_search} />
            </Field>

            {data.partners.length > 0 && (
              <Field label={I18n.t("partners.issuing_partner")} required info={lockedId && I18n.t(frozenId ? "partners.frozen_hint" : "partners.locked_hint")}>
                <Select
                  options      = {data.partners.map(partner => ({ value: partner.id, label: partner.name }))}
                  value        = {lockedId || partnerId}
                  onChange     = {e => setPartnerId(parseInt(e.target.value) || "")}
                  disabled     = {!!lockedId}
                  includeBlank
                  blankLabel   = {I18n.t("client.partners.select_partner")}
                  required
                />
              </Field>
            )}

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6">
                <Field label={I18n.t("workorders.wo_title", { defaultValue: "Titre" })} required>
                  <TextInput value={title} onChange={e => setTitle(e.target.value)} required />
                </Field>
              </div>
              <div className="col-span-3">
                <Field label={I18n.t("mbc_contracts.status.label", { defaultValue: "Statut" })} required>
                  <Select options={data.statuses} value={status} onChange={e => setStatus(e.target.value)} required />
                </Field>
              </div>
              {data.can_edit_code && (
                <div className="col-span-3">
                  <Field label={I18n.t("quotes.profitability.code", { defaultValue: "Code" })} required>
                    <TextInput value={contractCode} onChange={e => setContractCode(e.target.value)} required />
                  </Field>
                </div>
              )}
            </div>

            <Field label={I18n.t("maintenance.comment", { defaultValue: "Commentaire" })}>
              <textarea
                value     = {comment}
                onChange  = {e => setComment(e.target.value)}
                rows      = {2}
                className = "w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label={I18n.t("maintenance.operations_start_date", { defaultValue: "Début" })} required>
                <TextInput type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required />
              </Field>
              <Field label={I18n.t("maintenance.operations_end_date",   { defaultValue: "Fin" })}>
                <TextInput type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </Field>
            </div>
          </Card>

          <Card padding="md" className="rounded-lg space-y-4">
            <Field label={I18n.t("maintenance.revision_type", { defaultValue: "Type de révision" })}>
              <Select
                includeBlank
                options  = {data.revisions}
                value    = {annualRevision}
                onChange = {e => setAnnualRevision(e.target.value)}
              />
            </Field>

            {showRevision && (
              <>
                <Field label={I18n.t("maintenance.formula", { defaultValue: "Formule" })}>
                  <Select
                    includeBlank
                    options  = {data.formulas}
                    value    = {formulaId}
                    onChange = {e => setFormulaId(e.target.value)}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label={I18n.t("maintenance.frequency_revision", { defaultValue: "Fréquence" })}>
                    <Select
                      options  = {data.intervals.map(i => ({ value: i, label: i }))}
                      value    = {interval}
                      onChange = {e => setInterval(e.target.value)}
                    />
                  </Field>
                  <Field label="&nbsp;">
                    <Select
                      options  = {data.frequencies}
                      value    = {frequency}
                      onChange = {e => setFrequency(e.target.value)}
                    />
                  </Field>
                </div>
              </>
            )}
          </Card>

          <Card className="rounded-lg">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">
                {I18n.t("mbc_contracts.off_bpu_tiers.title", { defaultValue: "Tarification hors BPU" })}
              </h2>
              <button
                type      = "button"
                onClick   = {addTier}
                className = "inline-flex items-center gap-2 h-8 px-3 rounded-md bg-white border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <FontAwesomeIcon icon="plus" className="text-[11px]" />
                {I18n.t("mbc_contracts.off_bpu_tiers.add", { defaultValue: "Ajouter une tranche" })}
              </button>
            </div>

            {visibleTiers.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400">
                {I18n.t("mbc_contracts.off_bpu_tiers.none", { defaultValue: "Aucune tranche" })}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/50 text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                    <tr>
                      <th className="text-left px-4 py-2.5">{I18n.t("mbc_contracts.off_bpu_tiers.min_purchase_price")}</th>
                      <th className="text-left px-4 py-2.5">{I18n.t("mbc_contracts.off_bpu_tiers.max_purchase_price")}</th>
                      <th className="text-left px-4 py-2.5">{I18n.t("mbc_contracts.off_bpu_tiers.coefficient")}</th>
                      <th className="px-2 py-2.5 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tiers.map((tier, idx) => {
                      if (tier._destroy) return null
                      return (
                        <tr key={tier.id || tier._temp || idx} className="border-t border-slate-100">
                          <td className="px-4 py-2">
                            <TextInput type="number" step="0.01" value={tier.min_purchase_price} onChange={e => updateTier(idx, "min_purchase_price", e.target.value)} required />
                          </td>
                          <td className="px-4 py-2">
                            <TextInput type="number" step="0.01" value={tier.max_purchase_price ?? ""} onChange={e => updateTier(idx, "max_purchase_price", e.target.value)} />
                          </td>
                          <td className="px-4 py-2">
                            <TextInput type="number" step="0.001" value={tier.coefficient} onChange={e => updateTier(idx, "coefficient", e.target.value)} required />
                          </td>
                          <td className="px-2 py-2 text-center">
                            <button
                              type      = "button"
                              onClick   = {() => removeTier(idx)}
                              className = "inline-flex items-center justify-center w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                              title     = {I18n.t("button.delete", { defaultValue: "Supprimer" })}
                            >
                              <FontAwesomeIcon icon="trash" className="text-[12px]" />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <div className="flex items-center justify-end gap-2 pt-2">
            <a
              href      = {data.paths.cancel}
              className = "inline-flex items-center gap-2 h-9 px-4 rounded-md bg-white border border-slate-200 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
            >
              {I18n.t("button.cancel", { defaultValue: "Annuler" })}
            </a>
            <button
              type      = "submit"
              disabled  = {submitting}
              className = "inline-flex items-center gap-2 h-9 px-4 rounded-md bg-brand-500 text-[13px] font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {submitting && <FontAwesomeIcon icon="circle-notch" className="text-[12px] animate-spin" />}
              {I18n.t("button.save", { defaultValue: "Enregistrer" })}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default App
